import { create } from 'zustand';
import { restaurantService } from '@services/restaurantService';
import { menuService, type Category, type Product } from '@services/menuService';
import {
  restaurantOrderService,
  type OrderFilters,
} from '@services/restaurantOrderService';
import type { Restaurant, Order } from '@/types';

/**
 * RestaurantStore - Zustand Store for Restaurant Management Panel
 *
 * Per Restaurant Management Panel: State management
 * Follows pattern from authStore.ts and cartStore.ts
 *
 * STATE STRUCTURE:
 * - restaurant: Restaurant data
 * - categories: Category list
 * - products: Product list
 * - orders: Order list
 * - stats: Statistics data
 * - Loading and error states
 */
interface RestaurantState {
  // Data
  restaurant: Restaurant | null;
  categories: Category[];
  products: Product[];
  orders: Order[];
  lastOrderListFilters: OrderFilters | undefined;
  stats: {
    totalOrders: number;
    totalRevenue: number;
    averageRating: number;
    reviewCount: number;
  } | null;
  topProducts: Array<{
    productId: string | null;
    productName: string;
    totalQuantity: number;
    totalRevenue: number;
    orderCount: number;
  }>;
  revenueChartData: Array<{ date: string; revenue: number; orders: number }>;
  workingHours: Array<{
    id: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    restaurantId: string;
    createdAt: string;
    updatedAt: string;
  }>;

  isLoadingRestaurant: boolean;
  isLoadingCategories: boolean;
  isLoadingProducts: boolean;
  isLoadingOrders: boolean;
  isLoadingStats: boolean;

  error: string | null;

  loadRestaurant: () => Promise<void>;
  updateRestaurant: (dto: Partial<Restaurant>) => Promise<void>;
  uploadLogo: (file: File) => Promise<void>;
  uploadBanner: (file: File) => Promise<void>;
  toggleAcceptingOrders: (isAcceptingOrders: boolean) => Promise<void>;

  loadCategories: () => Promise<void>;
  createCategory: (dto: any) => Promise<Category>;
  updateCategory: (categoryId: string, dto: Partial<Category>) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;
  reorderCategory: (categoryId: string, newOrder: number) => Promise<void>;

  loadProducts: () => Promise<void>;
  createProduct: (categoryId: string, dto: any) => Promise<Product>;
  updateProduct: (productId: string, dto: Partial<Product>) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;
  toggleProductAvailability: (productId: string, isAvailable: boolean) => Promise<void>;
  reorderProduct: (productId: string, newOrder: number) => Promise<void>;
  uploadProductImage: (productId: string, file: File) => Promise<void>;
  uploadCategoryImage: (categoryId: string, file: File) => Promise<void>;
  updateProductStock: (productId: string, stockQuantity: number | null, lowStockThreshold?: number | null) => Promise<void>;
  getLowStockProducts: () => Promise<Product[]>;

  loadOrders: (
    filters?: OrderFilters,
    options?: { persistListFilters?: boolean; skipLoadingIndicator?: boolean },
  ) => Promise<void>;
  upsertOrderFromRealtime: (order: Order) => void;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  rejectOrder: (orderId: string, reason: string, message?: string) => Promise<void>;
  loadStats: (period?: 'today' | 'week' | 'month') => Promise<void>;
  loadTopProducts: (period?: 'today' | 'week' | 'month') => Promise<void>;
  loadRevenueChartData: (period?: 'today' | 'week' | 'month') => Promise<void>;
  loadWorkingHours: () => Promise<void>;
  updateWorkingHours: (hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>) => Promise<void>;

  clearError: () => void;
  reset: () => void;
}

export const useRestaurantStore = create<RestaurantState>((set, get) => ({
  restaurant: null,
  categories: [],
  products: [],
  orders: [],
  lastOrderListFilters: undefined,
  stats: null,
  topProducts: [],
  revenueChartData: [],
  workingHours: [],
  isLoadingRestaurant: false,
  isLoadingCategories: false,
  isLoadingProducts: false,
  isLoadingOrders: false,
  isLoadingStats: false,
  error: null,

  // ============================================================
  // RESTAURANT OPERATIONS
  // ============================================================

  loadRestaurant: async () => {
    set({ isLoadingRestaurant: true, error: null });
    try {
      const restaurant = await restaurantService.getMyRestaurant();
      set({ restaurant, isLoadingRestaurant: false });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Restaurant yüklenirken bir hata oluştu.';
      set({ error: errorMessage, isLoadingRestaurant: false });
      throw err;
    }
  },

  updateRestaurant: async (dto: Partial<Restaurant>) => {
    set({ error: null });
    try {
      const restaurant = await restaurantService.updateRestaurant(dto);
      set({ restaurant });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Restoran güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  uploadLogo: async (file: File) => {
    set({ error: null });
    try {
      const result = await restaurantService.uploadLogo(file);
      const restaurant = get().restaurant;
      if (restaurant) {
        set({ restaurant: { ...restaurant, logoUrl: result.url } });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Logo yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  uploadBanner: async (file: File) => {
    set({ error: null });
    try {
      const result = await restaurantService.uploadBanner(file);
      const restaurant = get().restaurant;
      if (restaurant) {
        set({ restaurant: { ...restaurant, bannerUrl: result.url } });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Banner yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  toggleAcceptingOrders: async (isAcceptingOrders: boolean) => {
    set({ error: null });
    try {
      const restaurant = await restaurantService.toggleAcceptingOrders(isAcceptingOrders);
      set({ restaurant });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Durum güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  // ============================================================
  // CATEGORY OPERATIONS
  // ============================================================

  loadCategories: async () => {
    set({ isLoadingCategories: true, error: null });
    try {
      const categories = await menuService.getCategories();
      set({ categories, isLoadingCategories: false });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Kategoriler yüklenirken bir hata oluştu.';
      set({ error: errorMessage, isLoadingCategories: false });
      throw err;
    }
  },

  createCategory: async (dto: any) => {
    set({ error: null });
    try {
      const category = await menuService.createCategory(dto);
      set((state) => ({ categories: [...state.categories, category] }));
      return category;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Kategori oluşturulurken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  updateCategory: async (categoryId: string, dto: Partial<Category>) => {
    set({ error: null });
    try {
      const category = await menuService.updateCategory(categoryId, dto);
      set((state) => ({
        categories: state.categories.map((c) => (c.id === categoryId ? category : c)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Kategori güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  deleteCategory: async (categoryId: string) => {
    set({ error: null });
    try {
      await menuService.deleteCategory(categoryId);
      set((state) => ({
        categories: state.categories.filter((c) => c.id !== categoryId),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Kategori silinirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  reorderCategory: async (categoryId: string, newOrder: number) => {
    set({ error: null });
    try {
      const category = await menuService.reorderCategory(categoryId, newOrder);
      set((state) => ({
        categories: state.categories.map((c) => (c.id === categoryId ? category : c)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Kategori sırası güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  // ============================================================
  // PRODUCT OPERATIONS
  // ============================================================

  loadProducts: async () => {
    set({ isLoadingProducts: true, error: null });
    try {
      const products = await menuService.getProducts();
      set({ products, isLoadingProducts: false });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürünler yüklenirken bir hata oluştu.';
      set({ error: errorMessage, isLoadingProducts: false });
      throw err;
    }
  },

  createProduct: async (categoryId: string, dto: any) => {
    set({ error: null });
    try {
      const product = await menuService.createProduct(categoryId, dto);
      set((state) => ({ products: [...state.products, product] }));
      return product;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün oluşturulurken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  updateProduct: async (productId: string, dto: Partial<Product>) => {
    set({ error: null });
    try {
      const product = await menuService.updateProduct(productId, dto);
      set((state) => ({
        products: state.products.map((p) => (p.id === productId ? product : p)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  deleteProduct: async (productId: string) => {
    set({ error: null });
    try {
      await menuService.deleteProduct(productId);
      set((state) => ({
        products: state.products.filter((p) => p.id !== productId),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün silinirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  toggleProductAvailability: async (productId: string, isAvailable: boolean) => {
    set({ error: null });
    try {
      const product = await menuService.toggleProductAvailability(productId, isAvailable);
      set((state) => ({
        products: state.products.map((p) => (p.id === productId ? product : p)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün durumu güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  reorderProduct: async (productId: string, newOrder: number) => {
    set({ error: null });
    try {
      const product = await menuService.reorderProduct(productId, newOrder);
      set((state) => ({
        products: state.products.map((p) => (p.id === productId ? product : p)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Ürün sırası güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  uploadProductImage: async (productId: string, file: File) => {
    set({ error: null });
    try {
      const result = await menuService.uploadProductImage(productId, file);
      set((state) => ({
        products: state.products.map((p) =>
          p.id === productId ? { ...p, imageUrl: result.url } : p,
        ),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Resim yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  uploadCategoryImage: async (categoryId: string, file: File) => {
    set({ error: null });
    try {
      const result = await menuService.uploadCategoryImage(categoryId, file);
      set((state) => ({
        categories: state.categories.map((c) =>
          c.id === categoryId ? { ...c, imageUrl: result.url } : c,
        ),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Resim yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  updateProductStock: async (productId: string, stockQuantity: number | null, lowStockThreshold?: number | null) => {
    set({ error: null });
    try {
      const product = await menuService.updateProductStock(productId, stockQuantity, lowStockThreshold);
      set((state) => ({
        products: state.products.map((p) => (p.id === productId ? product : p)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Stok güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  getLowStockProducts: async () => {
    set({ error: null });
    try {
      const products = await menuService.getLowStockProducts();
      return products;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Düşük stoklu ürünler yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  // ============================================================
  // ORDER OPERATIONS
  // ============================================================

  loadOrders: async (
    filters?: OrderFilters,
    options?: { persistListFilters?: boolean; skipLoadingIndicator?: boolean },
  ) => {
    const persistListFilters = options?.persistListFilters !== false;
    const skipLoadingIndicator = options?.skipLoadingIndicator === true;
    const state = get();
    const effectiveFilters =
      filters !== undefined ? filters : state.lastOrderListFilters ?? {};
    if (filters !== undefined && persistListFilters) {
      set({ lastOrderListFilters: filters });
    }
    if (!skipLoadingIndicator) {
      set({ isLoadingOrders: true, error: null });
    }
    try {
      const result = await restaurantOrderService.getRestaurantOrders(effectiveFilters);
      set({ orders: result.orders, isLoadingOrders: false });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Siparişler yüklenirken bir hata oluştu.';
      set({ error: errorMessage, isLoadingOrders: false });
      throw err;
    }
  },

  upsertOrderFromRealtime: (order: Order) => {
    set((state) => {
      const idx = state.orders.findIndex((o) => o.id === order.id);
      if (idx >= 0) {
        const next = [...state.orders];
        next[idx] = order;
        return { orders: next };
      }
      return { orders: [order, ...state.orders] };
    });
  },

  updateOrderStatus: async (orderId: string, status: Order['status']) => {
    set({ error: null });
    try {
      const order = await restaurantOrderService.updateOrderStatus(orderId, status);
      set((state) => ({
        orders: state.orders.map((o) => (o.id === orderId ? order : o)),
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Sipariş durumu güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  rejectOrder: async (orderId: string, reason: string, message?: string) => {
    set({ error: null });
    try {
      await restaurantOrderService.rejectOrder(
        orderId,
        reason as any,
        message,
      );
      await get().loadOrders();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Sipariş reddedilirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  loadStats: async (period: 'today' | 'week' | 'month' = 'today') => {
    set({ isLoadingStats: true, error: null });
    try {
      const stats = await restaurantService.getStats(period);
      set({ stats, isLoadingStats: false });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'İstatistikler yüklenirken bir hata oluştu.';
      set({ error: errorMessage, isLoadingStats: false });
      throw err;
    }
  },

  loadTopProducts: async (period: 'today' | 'week' | 'month' = 'today') => {
    set({ error: null });
    try {
      const topProducts = await restaurantService.getTopProducts(period, 10);
      set({ topProducts });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'En çok satan ürünler yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  loadRevenueChartData: async (period: 'today' | 'week' | 'month' = 'today') => {
    set({ error: null });
    try {
      const revenueChartData = await restaurantService.getRevenueChartData(period);
      set({ revenueChartData });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Gelir grafik verileri yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  loadWorkingHours: async () => {
    set({ error: null });
    try {
      const workingHours = await restaurantService.getWorkingHours();
      set({ workingHours });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Çalışma saatleri yüklenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  updateWorkingHours: async (hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>) => {
    set({ error: null });
    try {
      const workingHours = await restaurantService.updateWorkingHours(hours);
      set({ workingHours });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Çalışma saatleri güncellenirken bir hata oluştu.';
      set({ error: errorMessage });
      throw err;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    restaurant: null,
    categories: [],
    products: [],
    orders: [],
    lastOrderListFilters: undefined,
    stats: null,
    topProducts: [],
    revenueChartData: [],
    workingHours: [],
    isLoadingRestaurant: false,
    isLoadingCategories: false,
    isLoadingProducts: false,
    isLoadingOrders: false,
    isLoadingStats: false,
    error: null,
  }),
}));
